import { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import holdToTalkMacosVideoUrl from "@/assets/images/onboarding/mem-agent-shortcut-onboarding-macos.mp4";
import holdToTalkMacosImageUrl from "@/assets/images/onboarding/mem-agent-hold-to-talk-macos.png";
import holdToTalkWindowsVideoUrl from "@/assets/images/onboarding/mem-agent-shortcut-onboarding-windows.mp4";
import { MemAgentShortcutDropdown } from "@/components/mem-agent/mem-agent-shortcut-dropdown";
import {
  FooterSkipButton,
  type StepRenderProps,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { clientEnvModule } from "@/modules/client-env";
import {
  getDefaultMemAgentHoldToTalkKey,
  type MemAgentHoldToTalkKey,
} from "@/modules/mem-agent/hold-to-talk-key";
import { toastModule } from "@/modules/toast";
import { useAppStore } from "@/store";
import { usePrepareMemAgentFirstMessageOnboarding } from "./mem-agent-first-message-step";

const MEM_AGENT_SHORTCUT_SKIP_DELAY_MS = 45_000;

export interface MemAgentShortcutStepViewProps {
  holdToTalkKey: MemAgentHoldToTalkKey;
  isMacOs?: boolean;
  isSavingHoldToTalkKey?: boolean;
  isWindows?: boolean;
  onHoldToTalkKeyChange: (nextKey: MemAgentHoldToTalkKey) => void;
  onSkipForNow?: () => void;
}

export const MemAgentShortcutStep = observer(({ onComplete }: StepRenderProps) => {
  usePrepareMemAgentFirstMessageOnboarding();
  const { store } = useAppStore();
  const holdToTalkKey =
    store.spaceAccountSettings.memAgent.holdToTalkKey ?? getDefaultMemAgentHoldToTalkKey();
  const [displayHoldToTalkKey, setDisplayHoldToTalkKey] =
    useState<MemAgentHoldToTalkKey>(holdToTalkKey);
  const [isSavingHoldToTalkKey, setIsSavingHoldToTalkKey] = useState(false);
  const [canSkipForNow, setCanSkipForNow] = useState(false);

  useEffect(() => {
    const skipTimerId = window.setTimeout(() => {
      setCanSkipForNow(true);
    }, MEM_AGENT_SHORTCUT_SKIP_DELAY_MS);

    return () => window.clearTimeout(skipTimerId);
  }, []);

  useEffect(() => {
    setDisplayHoldToTalkKey(holdToTalkKey);
  }, [holdToTalkKey]);

  const handleShortcutChange = useCallback(
    (nextKey: MemAgentHoldToTalkKey) => {
      if (isSavingHoldToTalkKey || nextKey === displayHoldToTalkKey) return;

      const previousHoldToTalkKey = displayHoldToTalkKey;
      setDisplayHoldToTalkKey(nextKey);
      setIsSavingHoldToTalkKey(true);
      void store.spaceAccountSettings
        .updateSettingsUsingSyncOperation({
          mem_agent: {
            hold_to_talk_key: nextKey,
          },
        })
        .then(
          () => setIsSavingHoldToTalkKey(false),
          () => {
            setDisplayHoldToTalkKey(previousHoldToTalkKey);
            setIsSavingHoldToTalkKey(false);
            toastModule.triggerToast({
              content: "Couldn't save hold-to-talk key. Please try again.",
            });
          }
        );
    },
    [displayHoldToTalkKey, isSavingHoldToTalkKey, store.spaceAccountSettings]
  );

  return (
    <MemAgentShortcutStepView
      holdToTalkKey={displayHoldToTalkKey}
      isSavingHoldToTalkKey={isSavingHoldToTalkKey}
      onSkipForNow={canSkipForNow ? onComplete : undefined}
      onHoldToTalkKeyChange={handleShortcutChange}
    />
  );
});

/**
 * Presents the shortcut selection screen after Proactive checkout unlocks Mem Agent onboarding.
 * The user leaves this step by interacting with the floating agent, not by pressing a page button.
 */
export const MemAgentShortcutStepView = ({
  holdToTalkKey,
  isMacOs,
  isSavingHoldToTalkKey = false,
  isWindows,
  onHoldToTalkKeyChange,
  onSkipForNow,
}: MemAgentShortcutStepViewProps) => {
  const holdToTalkMedia = getHoldToTalkMedia({ isMacOs, isWindows });

  return (
    <ShortcutStepWrapper>
      <ShortcutTitle variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
        First, tell Mem Agent something to remember
      </ShortcutTitle>

      <ShortcutSlot data-test-id="mem-agent-shortcut-scroll-slot">
        {holdToTalkMedia.kind === "video" ? (
          <ShortcutVideoContainer $aspectRatio={holdToTalkMedia.aspectRatio}>
            <ShortcutVideo
              src={holdToTalkMedia.url}
              aria-label="Mem Agent hold-to-talk preview"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
            />
          </ShortcutVideoContainer>
        ) : (
          <ShortcutImageContainer>
            <ShortcutImage src={holdToTalkMedia.url} alt="Mem Agent hold-to-talk preview" />
          </ShortcutImageContainer>
        )}

        <ShortcutBody>
          <ShortcutPromptRow>
            <ShortcutCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
              Press and hold
            </ShortcutCopy>
            <MemAgentShortcutDropdown
              value={holdToTalkKey}
              isSaving={isSavingHoldToTalkKey}
              isWindows={isWindows}
              onChange={onHoldToTalkKeyChange}
            />
          </ShortcutPromptRow>

          <ShortcutCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
            and say it out loud. This is Push-to-Remember—the fastest way to get something off your
            mind and into Mem.
          </ShortcutCopy>

          <ShortcutCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
            Can’t talk right now? Click the floating Mem Agent icon and type instead.
          </ShortcutCopy>
        </ShortcutBody>
      </ShortcutSlot>

      {onSkipForNow !== undefined && (
        <ShortcutFooter>
          <FooterSkipButton onPress={onSkipForNow}>Skip for now</FooterSkipButton>
        </ShortcutFooter>
      )}
    </ShortcutStepWrapper>
  );
};

const getHoldToTalkMedia = ({ isMacOs, isWindows }: { isMacOs?: boolean; isWindows?: boolean }) => {
  const shouldShowWindowsVideo = isWindows ?? clientEnvModule.isWindows();

  if (shouldShowWindowsVideo) {
    return { aspectRatio: "107 / 54", kind: "video", url: holdToTalkWindowsVideoUrl } as const;
  }

  const shouldShowMacosVideo = isMacOs ?? clientEnvModule.isMacOs();

  if (shouldShowMacosVideo) {
    return { aspectRatio: "16 / 9", kind: "video", url: holdToTalkMacosVideoUrl } as const;
  }

  return { kind: "image", url: holdToTalkMacosImageUrl } as const;
};

const ShortcutStepWrapper = styled(WizardContentWrapper)`
  gap: 0;
  max-height: calc(
    min(720px, 95dvh) - ${({ theme }) => theme.spacing[7]} * 3 - ${({ theme }) => theme.spacing[2]}
  );
  overflow: hidden;
`;

const ShortcutTitle = styled(MDSText)`
  display: block;
  flex: 0 0 auto;
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.text.contrast};
  word-break: break-word;
`;

const ShortcutSlot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 20px;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const ShortcutBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ShortcutPromptRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ShortcutCopy = styled(MDSText)`
  display: block;
`;

const ShortcutFooter = styled.div`
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing[2]};
`;

const ShortcutImageContainer = styled.div`
  display: flex;
  width: 100%;
  aspect-ratio: 416 / 189;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const ShortcutImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const ShortcutVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const ShortcutVideoContainer = styled(ShortcutImageContainer)<{ $aspectRatio: string }>`
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
`;
